import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowUp, ArrowDown, Eye, EyeOff, Star } from "lucide-react";
import { CoverMedia } from "@/components/CoverMedia";
import { api, apiError } from "@/lib/api";
import { Switch } from "@/components/ui/switch";

export const SECTION_LABELS = {
  why: "Why XMD (studio highlights)",
  services: "Our Services",
  featured: "Featured — Recent Productions",
  testimonials: "Testimonials / Client Reviews",
  cta: "Book Your Session call-to-action",
};

export const sortFeatured = (items) =>
  items.filter((p) => p.featured).map((p, i) => ({ p, i }))
    .sort((a, b) => (a.p.featured_order ?? 1e6 + a.i) - (b.p.featured_order ?? 1e6 + b.i)).map((x) => x.p);

const FeaturedOrderEditor = () => {
  const qc = useQueryClient();
  const { data: portfolio = [] } = useQuery({ queryKey: ["portfolio"], queryFn: () => api.get("/portfolio").then((r) => r.data) });
  const items = sortFeatured(portfolio);
  const move = async (i, d) => {
    const j = i + d;
    if (j < 0 || j >= items.length) return;
    const arr = [...items];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    try { await api.post("/portfolio/featured-reorder", arr.map((p) => p.id)); toast.success("Featured order saved"); }
    catch (e) { toast.error(apiError(e)); }
    qc.invalidateQueries({ queryKey: ["portfolio"] });
    qc.invalidateQueries({ queryKey: ["portfolio-admin"] });
  };
  return (
    <div className="mt-10 max-w-2xl" data-testid="featured-order-editor">
      <h3 className="font-display text-xl text-white flex items-center gap-2"><Star size={16} className="text-[#D4AF37] fill-[#D4AF37]" /> Featured Order</h3>
      <p className="text-white/40 text-xs mt-1 mb-4">Sets the order of “Recent Productions” on the home page only — the first 4 are shown. Mark projects as Featured in the Portfolio tab.</p>
      {items.length === 0 && <p className="text-white/40 text-sm" data-testid="featured-order-empty">No featured projects yet.</p>}
      <div className="space-y-2">
        {items.map((p, i) => (
          <div key={p.id} className={`bg-[#111] border rounded-xl p-2 pr-4 flex items-center gap-3 ${i < 4 ? "border-white/10" : "border-white/5 opacity-50"}`} data-testid={`featured-order-row-${i}`}>
            <div className="relative h-12 w-16 rounded-lg overflow-hidden shrink-0"><CoverMedia item={p} playSize="sm" /></div>
            <span className="font-mono-label text-[#D4AF37] text-xs w-5">{i + 1}</span>
            <span className="flex-1 min-w-0 text-white text-sm truncate">{p.title}</span>
            {i >= 4 && <span className="text-[10px] uppercase text-white/40 font-mono-label">Not shown</span>}
            <div className="flex flex-col">
              <button onClick={() => move(i, -1)} disabled={i === 0} data-testid={`featured-up-${i}`} className="text-white/50 hover:text-[#D4AF37] disabled:opacity-20 p-0.5"><ArrowUp size={14} /></button>
              <button onClick={() => move(i, 1)} disabled={i === items.length - 1} data-testid={`featured-down-${i}`} className="text-white/50 hover:text-[#D4AF37] disabled:opacity-20 p-0.5"><ArrowDown size={14} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const useHomeLayout = () =>
  useQuery({ queryKey: ["home-layout"], queryFn: () => api.get("/home-layout").then((r) => r.data.sections) });

export const HomeLayoutEditor = () => {
  const qc = useQueryClient();
  const { data: sections = [] } = useHomeLayout();
  const save = async (next) => {
    qc.setQueryData(["home-layout"], next);
    try { await api.put("/home-layout", { sections: next }); toast.success("Home page updated"); }
    catch (e) { toast.error(apiError(e)); }
    qc.invalidateQueries({ queryKey: ["home-layout"] });
  };
  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= sections.length) return;
    const arr = [...sections];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    save(arr);
  };
  const toggle = (i, visible) => save(sections.map((s, k) => (k === i ? { ...s, visible } : s)));

  return (
    <div data-testid="home-layout-editor">
      <p className="text-white/40 text-xs mb-4">The hero always stays on top. Use the arrows to reorder the sections below it and the switch to show or hide each one.</p>
      <div className="space-y-2 max-w-2xl">
        <div className="bg-[#0d0d0d] border border-dashed border-white/10 rounded-xl px-4 py-3 text-white/40 text-sm">Hero (logo, booking buttons, contact chips) — fixed</div>
        {sections.map((s, i) => (
          <div key={s.key} className={`bg-[#111] border rounded-xl px-4 py-3 flex items-center gap-3 transition-opacity ${s.visible ? "border-white/10" : "border-white/5 opacity-50"}`} data-testid={`home-section-${s.key}`}>
            <span className="font-mono-label text-[#D4AF37] text-xs w-5">{i + 1}</span>
            <span className="flex-1 text-white text-sm">{SECTION_LABELS[s.key] || s.key}</span>
            {s.visible ? <Eye size={14} className="text-white/40" /> : <EyeOff size={14} className="text-white/40" />}
            <Switch checked={s.visible} onCheckedChange={(v) => toggle(i, v)} data-testid={`home-section-toggle-${s.key}`} />
            <div className="flex flex-col">
              <button onClick={() => move(i, -1)} disabled={i === 0} data-testid={`home-section-up-${s.key}`} className="text-white/50 hover:text-[#D4AF37] disabled:opacity-20 p-0.5"><ArrowUp size={14} /></button>
              <button onClick={() => move(i, 1)} disabled={i === sections.length - 1} data-testid={`home-section-down-${s.key}`} className="text-white/50 hover:text-[#D4AF37] disabled:opacity-20 p-0.5"><ArrowDown size={14} /></button>
            </div>
          </div>
        ))}
      </div>
      <FeaturedOrderEditor />
    </div>
  );
};
